// Platform administration contracts (modules-and-api.md §5.1; TEN-03, TEN-04, TEN-07, TEN-12, TEN-39; UC-TENANCY-001, UC-TENANCY-011).
// The only surface where a tenant id appears in a path.
import { z } from "zod";
import { PASSWORD_MAX_LENGTH } from "./auth";
import { EmailSchema, InstantSchema, UlidSchema } from "./primitives";
import { COMMERCIAL_MODELS, ORGANISATION_STATUSES } from "./tenancy";

export const PlatformLoginRequestSchema = z.strictObject({
  email: EmailSchema,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export const PlatformAdminViewSchema = z.object({ id: UlidSchema, email: z.string() });
export const PlatformLoginResponseSchema = z.object({ admin: PlatformAdminViewSchema, expiresAt: InstantSchema });

/** BR-TENANCY-003: a franchisor link is present if and only if the model is Franchisee. */
export function franchisorLinkErrors(v: { commercialModel?: string | undefined; franchisorOrgId?: string | null | undefined }): { path: string; code: string; message: string }[] {
  if (v.commercialModel === undefined) return [];
  if (v.commercialModel === "Franchisee" && !v.franchisorOrgId) return [{ path: "franchisorOrgId", code: "required", message: "a Franchisee names its Franchisor organisation" }];
  if (v.commercialModel !== "Franchisee" && v.franchisorOrgId) return [{ path: "franchisorOrgId", code: "not_allowed", message: "only a Franchisee names a franchisor" }];
  return [];
}

export const ProvisionTenantInputSchema = z.strictObject({
  legalName: z.string().trim().min(1).max(200),
  tradingName: z.string().trim().min(1).max(200).optional(),
  commercialModel: z.enum(COMMERCIAL_MODELS),
  franchisorOrgId: UlidSchema.optional(),
  owner: z.strictObject({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    email: EmailSchema,
    phone: z.string().trim().max(40).optional(),
  }),
}).superRefine((v, ctx) => {
  for (const e of franchisorLinkErrors(v)) ctx.addIssue({ code: "custom", path: [e.path], message: e.message, params: { code: e.code } });
});
export type ProvisionTenantInput = z.infer<typeof ProvisionTenantInputSchema>;

export const ProvisionTenantResponseSchema = z.object({
  orgId: UlidSchema,
  ownerInvitationSentTo: z.string(),
  ownerUserId: UlidSchema,
  invitationExpiresAt: InstantSchema,
  /** Present only outside production so tests and staging bootstrap can accept the first Owner invitation. */
  invitationToken: z.string().optional(),
});

export const TenantPatchSchema = z.strictObject({
  legalName: z.string().trim().min(1).max(200).optional(),
  tradingName: z.string().trim().min(1).max(200).nullable().optional(),
  commercialModel: z.enum(COMMERCIAL_MODELS).optional(),
  franchisorOrgId: UlidSchema.nullable().optional(),
  status: z.enum(ORGANISATION_STATUSES).optional(),
});
export type TenantPatch = z.infer<typeof TenantPatchSchema>;

export const TenantViewSchema = z.object({
  orgId: UlidSchema,
  legalName: z.string(),
  tradingName: z.string().nullable(),
  commercialModel: z.enum(COMMERCIAL_MODELS),
  franchisorOrgId: UlidSchema.nullable(),
  status: z.enum(ORGANISATION_STATUSES),
  shardBinding: z.string(),
  jurisdiction: z.string(),
  country: z.string(), currency: z.string(), locale: z.string(), timezone: z.string(), vatRateBp: z.number().int(),
  branchCount: z.number().int(),
  userCount: z.number().int(),
  version: z.number().int(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type TenantView = z.infer<typeof TenantViewSchema>;

export const TenantListItemSchema = z.object({
  orgId: UlidSchema, legalName: z.string(), status: z.enum(ORGANISATION_STATUSES), shardBinding: z.string(), jurisdiction: z.string(), createdAt: InstantSchema,
});

export const PlatformHealthSchema = z.object({
  ok: z.boolean(),
  environment: z.string(),
  platformDb: z.enum(["ok", "error"]),
  shards: z.record(z.string(), z.enum(["ok", "error", "unbound"])),
  tenants: z.number().int(),
  checkedAt: InstantSchema,
});
